function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import { get, noop, isFunction, isArray, join, map, compact, isEmpty, isNil } from 'lodash';
import InputField from "../InputField/InputField";
import Display from "./components/Display";
import EditField from "./components/EditField";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const InlineEditField = _ref => {
  let {
    afterLabelContent = null,
    className = '',
    disabled = false,
    displayValue = undefined,
    fieldComponent = InputField,
    fieldProps = {},
    id,
    label = '',
    value = '',
    hideLabel = false,
    preValueText = '',
    onFieldEdit = noop,
    onFieldCancel = noop,
    onFieldSave,
    onFieldError = noop,
    onFieldChange = noop,
    onFieldBlur = noop,
    fieldValidations = undefined
  } = _ref;
  const [editMode, setEditMode] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldMeta, setFieldMeta] = useState({
    valid: true,
    invalid: false,
    touched: false,
    pristine: true,
    dirty: false,
    visited: false,
    error: ''
  });
  const componentRef = useRef(null);
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  const validate = useCallback(() => {
    let error;
    if (isFunction(fieldValidations)) {
      error = fieldValidations(localValue);
    } else if (isArray(fieldValidations)) {
      const errors = map(fieldValidations, validation => validation(localValue));
      error = join(compact(errors), ', ');
    }
    if (componentRef.current && isFunction(componentRef.current.validate)) {
      const errors = componentRef.current.validate(localValue);
      error = join(compact([error, errors]), ', ');
    }
    setFieldMeta(prevMeta => _objectSpread(_objectSpread({}, prevMeta), {}, {
      error,
      valid: isEmpty(error),
      invalid: !isEmpty(error)
    }));
    return error;
  }, [fieldValidations, localValue]);
  const onEditClick = () => {
    setEditMode(true);
    validate();
    onFieldEdit();
  };
  const onCancelEdit = event => {
    event.preventDefault();
    setEditMode(false);
    setLocalValue(value);
    setFieldMeta(prevMeta => _objectSpread(_objectSpread({}, prevMeta), {}, {
      dirty: false,
      pristine: true,
      touched: false,
      visited: false
    }));
    validate();
    onFieldCancel();
  };
  const onSaveEdit = event => {
    event.preventDefault();
    setFieldMeta(prevMeta => _objectSpread(_objectSpread({}, prevMeta), {}, {
      touched: true,
      visited: true
    }));
    const error = validate();
    if (isEmpty(error)) {
      const result = onFieldSave(localValue);
      if (result instanceof Promise) {
        setIsSaving(true);
        result.then(() => {
          setIsSaving(false);
          setEditMode(false);
        });
      } else {
        setEditMode(false);
      }
    } else {
      onFieldError(error);
    }
  };
  const onChange = event => {
    const value = get(event, 'target.value', event);
    setLocalValue(value);
    setFieldMeta(prevMeta => _objectSpread(_objectSpread({}, prevMeta), {}, {
      dirty: true,
      pristine: false
    }));
    validate();
    onFieldChange(value);
  };
  const onBlur = () => {
    setFieldMeta(prevMeta => _objectSpread(_objectSpread({}, prevMeta), {}, {
      touched: true,
      visited: true
    }));
    validate();
    onFieldBlur();
  };
  const containerClass = cn({
    'flex-none': true
  }, className);
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass,
    id: id,
    children: [(!editMode || disabled) && /*#__PURE__*/_jsx(Display, {
      afterLabelContent: afterLabelContent,
      id: id,
      label: label,
      onEditClick: onEditClick,
      value: !isNil(displayValue) ? displayValue : value,
      disabled: disabled,
      hideLabel: hideLabel,
      preValueText: preValueText
    }), editMode && !disabled && /*#__PURE__*/_jsx(EditField, {
      afterLabelContent: afterLabelContent,
      FieldComponent: fieldComponent,
      fieldMeta: fieldMeta,
      fieldProps: fieldProps,
      id: id,
      isSaving: isSaving,
      label: label,
      onBlur: onBlur,
      onCancelEdit: onCancelEdit,
      onChange: onChange,
      onSaveEdit: onSaveEdit,
      setFieldRef: ref => componentRef.current = ref,
      value: localValue
    })]
  });
};
InlineEditField.propTypes = {
  afterLabelContent: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  displayValue: PropTypes.string,
  id: PropTypes.string.isRequired,
  fieldComponent: PropTypes.func,
  fieldProps: PropTypes.object,
  fieldValidations: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
  label: PropTypes.any,
  onFieldBlur: PropTypes.func,
  onFieldCancel: PropTypes.func,
  onFieldChange: PropTypes.func,
  onFieldEdit: PropTypes.func,
  onFieldError: PropTypes.func,
  onFieldSave: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  hideLabel: PropTypes.bool,
  preValueText: PropTypes.node
};
export default InlineEditField;
//# sourceMappingURL=InlineEditField.js.map